// Full-screen media lightbox — triggered by lightboxStore.open().
// Renders via React portal so it escapes any stacking context in the chat tree.
// Supports: image (full-size), video (player), audio (player), PDF (iframe),
// and a generic info card with download for everything else.
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Download, X } from 'lucide-react';
import { useLightboxItem } from '@/lib/lightboxStore';
import { getFileTypeStyle } from '@/lib/fileTypeIcon';

export function MediaLightbox() {
  const { item, close } = useLightboxItem();

  // Keyboard: Escape closes
  useEffect(() => {
    if (!item) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [item, close]);

  // Prevent body scroll while open
  useEffect(() => {
    if (!item) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [item]);

  if (!item) return null;

  const { Icon, color } = getFileTypeStyle(item.mimeType);
  const isImage = item.mimeType.startsWith('image/');
  const isVideo = item.mimeType.startsWith('video/');
  const isAudio = item.mimeType.startsWith('audio/');
  const isPdf = item.mimeType === 'application/pdf';
  const downloadUrl = item.downloadUrl ?? item.url;

  return createPortal(
    <div
      className="fixed inset-0 z-[90] flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label={`Preview: ${item.fileName}`}
    >
      {/* Clickable backdrop — pointer-events-auto so clicks on the dark area close the lightbox */}
      <div className="absolute inset-0 bg-black/88 backdrop-blur-sm" onClick={close} aria-hidden="true" />

      {/* Foreground — pointer-events-none on the layout wrapper, re-enabled on interactive children */}
      <div className="pointer-events-none relative flex h-full flex-col">
        {/* ── Header bar ─────────────────────────────────────────────────────── */}
        <div className="pointer-events-auto flex shrink-0 items-center gap-3 border-b border-white/10 bg-black/60 px-4 py-2.5 backdrop-blur-md">
          <Icon className={`h-4 w-4 shrink-0 ${color}`} />
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-white" title={item.fileName}>
            {item.fileName}
          </span>
          <a
            href={downloadUrl}
            download={item.fileName}
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-1.5 rounded-md bg-white/10 px-2.5 py-1 text-xs text-white/80 transition-colors hover:bg-white/20 hover:text-white"
          >
            <Download className="h-3.5 w-3.5" />
            Download
          </a>
          <button
            type="button"
            onClick={close}
            className="rounded-md bg-white/10 p-1.5 text-white/80 transition-colors hover:bg-white/20 hover:text-white"
            aria-label="Close preview"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* ── Content area ───────────────────────────────────────────────────── */}
        {isPdf ? (
          /* PDF: iframe fills available height — pointer-events-auto so the viewer is interactive */
          <div className="pointer-events-auto min-h-0 flex-1 p-2">
            <iframe
              src={`${item.url}#toolbar=1&navpanes=1&view=FitH`}
              title={item.fileName}
              sandbox="allow-same-origin"
              className="h-full w-full rounded-md border border-white/10 bg-white"
            />
          </div>
        ) : (
          /* Non-PDF: center content; dark padding area is "backdrop" and closes on click */
          <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto p-4">
            {isImage && (
              <img
                src={item.url}
                alt={item.fileName}
                className="pointer-events-auto max-h-full max-w-full rounded-md object-contain shadow-2xl"
                draggable={false}
              />
            )}

            {isVideo && (
              <video
                src={item.url}
                controls
                autoPlay
                className="pointer-events-auto max-h-full max-w-full rounded-md shadow-2xl"
              />
            )}

            {isAudio && (
              <div className="pointer-events-auto flex flex-col items-center gap-4 rounded-xl bg-white/5 px-8 py-10">
                <Icon className={`h-16 w-16 ${color}`} />
                <p className="max-w-xs truncate text-sm font-medium text-white">{item.fileName}</p>
                <audio src={item.url} controls autoPlay className="w-72 max-w-full" />
              </div>
            )}

            {!isImage && !isVideo && !isAudio && (
              <div className="pointer-events-auto flex flex-col items-center gap-4 rounded-xl bg-white/5 px-10 py-12">
                <Icon className={`h-16 w-16 ${color}`} />
                <p className="max-w-xs truncate text-lg font-medium text-white">{item.fileName}</p>
                <p className="text-sm text-white/50">Preview not available for this file type.</p>
                <a
                  href={downloadUrl}
                  download={item.fileName}
                  className="flex items-center gap-2 rounded-lg bg-white/10 px-4 py-2 text-sm text-white transition-colors hover:bg-white/20"
                >
                  <Download className="h-4 w-4" />
                  Download file
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
